<script setup>
import { computed, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import Policy from 'dashboard/components/policy.vue';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();
const store = useStore();

const [showActionsDropdown, toggleDropdown] = useToggle();

const excludeFromAi = ref(false);
const isUpdatingAiExclusion = ref(false);

const isWhatsAppInbox = computed(() => props.inbox.channel_type === INBOX_TYPES.WHATSAPP);

watch(
  () => props.inbox.whatsapp_group_exclude_from_ai,
  value => {
    excludeFromAi.value = value || false;
  },
  { immediate: true }
);

const handleAiExclusionChange = async value => {
  if (isUpdatingAiExclusion.value) return;

  isUpdatingAiExclusion.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.id,
      formData: false,
      whatsapp_group_exclude_from_ai: value,
    });
    useAlert(t('CAPTAIN.INBOXES.WHATSAPP_GROUP_AI_EXCLUSION.SUCCESS'));
  } catch (error) {
    excludeFromAi.value = !value;
    useAlert(t('CAPTAIN.INBOXES.WHATSAPP_GROUP_AI_EXCLUSION.ERROR'));
  } finally {
    isUpdatingAiExclusion.value = false;
  }
};

const inboxName = computed(() => {
  const inbox = props.inbox;
  if (!inbox?.name) {
    return '';
  }

  const isTwilioChannel = inbox.channel_type === INBOX_TYPES.TWILIO;
  const isWhatsAppChannel = inbox.channel_type === INBOX_TYPES.WHATSAPP;
  const isEmailChannel = inbox.channel_type === INBOX_TYPES.EMAIL;

  if (isTwilioChannel || isWhatsAppChannel) {
    const identifier = inbox.messaging_service_sid || inbox.phone_number;
    return identifier ? `${inbox.name} (${identifier})` : inbox.name;
  }

  if (isEmailChannel && inbox.email) {
    return `${inbox.name} (${inbox.email})`;
  }

  return inbox.name;
});

const menuItems = computed(() => [
  {
    label: t('CAPTAIN.INBOXES.OPTIONS.DISCONNECT'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const icon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium, 'outline');
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex justify-between w-full gap-1">
      <span class="text-base text-n-slate-12 line-clamp-1 flex items-center gap-2">
        <span :class="icon" />
        {{ inboxName }}
      </span>
      <div class="flex items-center gap-2">
        <Policy
          v-on-clickaway="() => toggleDropdown(false)"
          :permissions="['administrator']"
          class="relative flex items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full"
            @action="handleAction($event)"
          />
        </Policy>
      </div>
    </div>
    <div
      v-if="isWhatsAppInbox"
      class="flex items-center justify-between gap-4 pt-3 mt-1 border-t border-n-weak"
    >
      <div class="flex-1">
        <p class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.INBOXES.WHATSAPP_GROUP_AI_EXCLUSION.LABEL') }}
        </p>
        <p class="mt-0.5 text-xs text-n-slate-11">
          {{ t('CAPTAIN.INBOXES.WHATSAPP_GROUP_AI_EXCLUSION.DESCRIPTION') }}
        </p>
      </div>
      <ToggleSwitch v-model="excludeFromAi" @change="handleAiExclusionChange($event)" />
    </div>
  </CardLayout>
</template>
